import React from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import {
  DNABox,
  DNAButton,
  DNADivider,
  DNAText,
  Iffy,
  util,
} from '@alucio/lux-ui';
import { useMediaQuery } from 'react-responsive';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { CommonIcon, DNAChip, DNAPopover } from 'src/components/DNA';
import {
  usePublisherVersioningState,
  MATCH_SLIDE_STATUS,
} from 'src/state/machines/publisherVersioning/PublisherVersioningProvider';
import { useMatchSlidesState } from 'src/state/machines/publisherVersioning/MatchSlides/MatchSlidesProvider';
import CollapsibleCard from 'src/components/CollapsibleCard/CollapsibleCard';

const S = StyleSheet.create({
  countIcon: {
    padding: 6,
    marginHorizontal: 4,
  },
  columnHeaderContainer: {
    backgroundColor: colors['color-gray-80'],
    borderRadius: 8,
    width: 342,
  },
  columnHeaderContainerSmallerScreen: {
    backgroundColor: colors['color-gray-80'],
    borderRadius: 8,
    width: 242,
  },
  columnHeaderText: {
    color: colors['color-text-tertiary'],
    paddingVertical: 8,
  },
  headerContainer: {
    zIndex: 3,
    padding: 12,
    marginTop: 8,
    borderRadius: 8,
    backgroundColor: colors['color-text-basic'],
    borderColor: colors['color-gray-100'],
  },
  headerText: {
    paddingHorizontal: 41,
    paddingVertical: 32,
    textAlign: 'center',
  },
  helpStyle: {
    borderRadius: 4,
    borderColor: colors['color-gray-100'],
    backgroundColor: colors['color-text-basic'],
  },
  helpContentContainer: {
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  helpText: {
    lineHeight: 24,
  },
  helpContentContainerIndent: {
    marginVertical: 12,
    paddingLeft: 31,
  },
  helpTextIndent: {
    lineHeight: 24,
    textIndent: -15,
  },
  helpContainer: {
    width: 760,
    marginBottom: 30,
  },
  helpContainerSmallerScreen: {
    width: 586,
    marginBottom: 30,
  },
  tooltipText: {
    color: colors['color-text-white'],
  },
  hyperLink: {
    color: colors['color-primary-500'],
  },
})

const MatchSlidesHelp: React.FC = () => {
  const { helpVisible, setHelpVisible } = useMatchSlidesState()
  const isLargeScreen = useMediaQuery({
    query: '(min-width: 1300px)',
  })

  const navigateToHelpLink = () => {
    window.open(
      'https://alucio.zendesk.com/hc/en-us/articles/27976916232343-Slide-Matching-Process',
    )
  }

  return (
    <Iffy is={helpVisible}>
      <DNABox
        style={util.mergeStyles(
          undefined,
          isLargeScreen ? S.helpContainer : S.helpContainerSmallerScreen,
        )
          }
        spacing="sm"
        appearance="col"
        alignX="end"
      >
        <DNAButton
          appearance="outline"
          status="tertiary"
          iconLeft="x"
          onPress={() => setHelpVisible(false)}
        />
        <DNABox spacing="sm" fill appearance="col">
          <CollapsibleCard
            headerTitle={<DNAText b1 bold>Why should I match the slides?</DNAText>}
            isCollapsed={true}
            cardStyle={S.helpStyle}
          >
            <DNABox style={S.helpContentContainer}>
              <DNAText b1 numberOfLines={5} style={S.helpText}>
                {/* eslint-disable-next-line max-len */}
                When you match slides between versions, information about the slide is carried from version to version. This will allow viewers to automatically update their custom decks with the slides you have chosen, saving them time and effort. In addition, slide trends can be tracked over time.
              </DNAText>
            </DNABox>
          </CollapsibleCard>
          <CollapsibleCard
            headerTitle={<DNAText b1 bold>How do I match the slides?</DNAText>}
            isCollapsed={true}
            cardStyle={S.helpStyle}
          >
            <DNABox appearance="col" style={S.helpContentContainer}>
              {/* eslint-disable-next-line max-len */}
              <DNAText b1 style={S.helpText}>Beacon has automatically determined matches for your slides based on their similarity to the previous version. If there is no match, the slide on the left will be empty. If this is incorrect, a slide can be manually matched using the following steps:</DNAText>
              <DNABox appearance="col" style={S.helpContentContainerIndent}>
                <DNAText b1 style={S.helpTextIndent}>
                  {/* eslint-disable-next-line max-len */}
                  1. Click on the “Find slides” button in the top action bar to open the left slide list, or click on the “Find slide” button within the empty box.
                </DNAText>
                <DNAText b1 style={S.helpTextIndent}>
                  {/* eslint-disable-next-line max-len */}
                  2. From the slide roll, find the previous version slide that corresponds with the slide in the new version.
                </DNAText>
                <DNAText b1 style={S.helpTextIndent}>
                  3. Drag and drop that slide into the dotted slide container.
                </DNAText>
              </DNABox>
              <DNAText b1 style={S.helpText}>
                For further information including step-by-step instructions, click
                <TouchableOpacity onPress={navigateToHelpLink}>
                  <DNAText style={S.hyperLink}> here </DNAText>
                </TouchableOpacity>
                to access the full help article.
              </DNAText>
            </DNABox>
          </CollapsibleCard>
        </DNABox>
      </DNABox>
    </Iffy>
  )
}

const Header: React.FC = () => {
  const {
    slideCount,
    onUnmatch,
    slideGroupTargetRef,
    toggleSlidesGroupPoolVisible,
    onScrollToStatus,
    helpVisible,
    setHelpVisible,
  } = useMatchSlidesState();
  const { currentDocumentVersionORM, previousVersionORM } = usePublisherVersioningState();
  const isLargeScreen = useMediaQuery({
    query: '(min-width: 1300px)',
  })

  return (
    <DNABox appearance="col" alignX="center">
      {/* Header action bar */}
      <DNABox
        appearance="row"
        alignX="center"
        alignY="center"
        spacing="sm"
        style={S.headerContainer}
      >
        <DNAButton
          appearance="outline"
          status="tertiary"
          onPress={toggleSlidesGroupPoolVisible}
        >
          Find slides
        </DNAButton>
        <DNADivider vertical />
        <Iffy is={slideCount.needsReview}>
          <DNAButton
            iconLeft="warning-circle"
            appearance="outline"
            status="warning"
            onPress={() => onScrollToStatus(MATCH_SLIDE_STATUS.NEEDS_REVIEW)}
            children={`${isLargeScreen ? 'Needs Review' : ''} (${slideCount.needsReview})`}
          />
        </Iffy>
        <Iffy is={slideCount.noMatch}>
          <DNAButton
            appearance="outline"
            status="tertiary"
            iconLeft="not-equals"
            onPress={() => onScrollToStatus(MATCH_SLIDE_STATUS.NO_MATCH)}
            children={`${isLargeScreen ? 'No-match' : ''} (${slideCount.noMatch})`}
          />
        </Iffy>
        <DNAText b1 status="success">
          <CommonIcon variant="check" size={isLargeScreen ? 'lg' : 'md'} style={S.countIcon}/>
          {`${slideCount.total - slideCount.needsReview - slideCount.noMatch} of ${slideCount.total} matches`}
        </DNAText>
        <DNADivider vertical style={{ marginHorizontal: 4 }} />
        <DNAButton
          appearance="outline"
          status="tertiary"
          iconLeft={isLargeScreen ? undefined : 'not-equals'}
          onPress={() => onUnmatch('all')}
        >
          {isLargeScreen ? 'Unmatch all' : ''}
        </DNAButton>
        <DNAPopover disablePopover={['tabletPWA']}>
          <DNAPopover.Anchor>
            <DNAButton
              appearance="outline"
              status="tertiary"
              iconLeft="arrow-up"
              onPress={() => slideGroupTargetRef.current?.scrollToTop(true)}
            />
          </DNAPopover.Anchor>
          <DNAPopover.Content>
            <DNAText style={S.tooltipText} numberOfLines={1}>Scroll to top</DNAText>
          </DNAPopover.Content>
        </DNAPopover>
        <DNAPopover disablePopover={['tabletPWA']}>
          <DNAPopover.Anchor>
            <DNAButton
              appearance="outline"
              status="tertiary"
              iconLeft="question"
              onPress={() => setHelpVisible(!helpVisible)}
            />
          </DNAPopover.Anchor>
          <DNAPopover.Content>
            <DNAText style={S.tooltipText} numberOfLines={1}>Learn more</DNAText>
          </DNAPopover.Content>
        </DNAPopover>
      </DNABox>
      <DNABox alignX="center">
        <DNAText
          bold
          style={util.mergeStyles(
            undefined,
            S.headerText,
            { width: isLargeScreen ? 832 : 586 },
          )}
        >
          We've auto-matched slides to the previous version. Please review to make sure they all match.
        </DNAText>
      </DNABox>
      <MatchSlidesHelp />
      <DNABox appearance="row" spacing="xl" alignX="center" style={{ padding: 8 }}>
        <DNABox
          style={util.mergeStyles(
            undefined,
            isLargeScreen ? S.columnHeaderContainer : S.columnHeaderContainerSmallerScreen,
          )
          }
          alignX="center"
          spacing="sm"
        >
          <DNAText style={S.columnHeaderText}>
            {`Version ${previousVersionORM?.meta.version.semVerLabel}`}
          </DNAText>
          <DNABox style={{ paddingVertical: 8 }}>
            <DNAChip.Beacon appearance="filled" children="CURRENT" />
          </DNABox>
        </DNABox>
        <DNABox
          style={util.mergeStyles(
            undefined,
            isLargeScreen ? S.columnHeaderContainer : S.columnHeaderContainerSmallerScreen,
          )
          }
          alignX="center"
          spacing="sm"
        >
          <DNAText style={S.columnHeaderText}>
            {`${currentDocumentVersionORM?.meta.version.updateStatus === 'NOT_PUBLISHED'
              ? 'New Version'
              : currentDocumentVersionORM?.meta.version.semVerLabel}`}
          </DNAText>
          <DNABox style={{ paddingVertical: 8 }}>
            <DNAChip.Beacon appearance="filled" status="success" children="NEW" />
          </DNABox>
        </DNABox>
      </DNABox>
    </DNABox>
  )
}

export default Header
